/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import toNumber from 'lodash/toNumber';
import { addDays, differenceInDays, format, startOfDay, toDate } from 'date-fns';
import * as types from '../constants';
import { cloneState } from '../utils/stateUtils';
import { getSearchDocId } from '../utils/tripUtils';
import { isBetweenDays } from '../utils/dateUtils';

const INITIALSTATE = {};

// check if any of the ui filters are set
function checkUIFiltersPresent(state) {
    for (const propName in state.uiFilters) {
        if (isObject(state.uiFilters[propName])) {
            if (!isEmpty(state.uiFilters[propName])) {
                return true;
            }
        }
    }
    return false;
}

// copy search results details into trip item
function copyItemDetails(item, details) {
    const copiedItem = item;

    copiedItem.name = details.name;
    copiedItem.reservable = details.reservable;
    copiedItem.availability = details.availability;
    copiedItem.availability_counts = details.availability_counts;
    copiedItem.preview_image_url = details.preview_image_url;
    copiedItem.org_id = details.org_id;
    copiedItem.org_name = details.org_name;
    copiedItem.parent_id = details.parent_id;
    copiedItem.parent_name = details.parent_name;
    copiedItem.parent_type_name = details.parent_type_name;
    copiedItem.campsites_count = details.campsites_count;
    copiedItem.tours_count = details.tours_count;
    copiedItem.campsite_type_of_use = details.campsite_type_of_use;
    copiedItem.activities = details.activities;
    copiedItem.city = details.city;
    copiedItem.state_code = details.state_code;
    copiedItem.country_code = details.country_code;
    copiedItem.description = details.description;
    copiedItem.lat = toNumber(details.latitude);
    copiedItem.lng = toNumber(details.longitude);
    copiedItem.type = details.type;
    return copiedItem;
}

function enumerateDatesBetweenDates(startDate, endDate) {
    const dates = [];

    const currDate = startDate;
    const lastDate = endDate;

    dates.push(format(toDate(currDate), types.FNS_DATE_FORMAT));

    while (differenceInDays(addDays(currDate, 1), lastDate) < 0) {
        dates.push(format(toDate(currDate), types.FNS_DATE_FORMAT));
    }

    dates.push(format(toDate(lastDate), types.FNS_DATE_FORMAT));

    return dates;
}

// calculate reserved status for each trip item based on reservations
function checkTripReservationStatus(trip, reservations) {
    if (trip.start_date) {
        let startDate = startOfDay(trip.start_date);
        let endDate = null;

        trip.destinations.forEach((destination) => {
            if (destination.duration) {
                endDate = addDays(startDate, destination.duration);
            }

            if (startDate && endDate) {
                destination.items.forEach((i) => {
                    const item = i;
                    item.reserved = false;
                    item.status = null;
                    item.dates = [];

                    if (reservations.camping) {
                        reservations.camping.forEach((res) => {
                            if (
                                res.facility_id === item.entity_id &&
                                res.reservation_status === 'RESERVED'
                            ) {
                                const sdt = startOfDay(res.start_date);
                                const edt = startOfDay(res.end_date);

                                if (
                                    isBetweenDays(sdt, startDate, endDate) ||
                                    isBetweenDays(startDate, sdt, edt)
                                ) {
                                    item.reserved = true;
                                    item.status = 'Reserved';
                                    item.dates.push(
                                        ...enumerateDatesBetweenDates(sdt, edt)
                                    );
                                }
                            }
                        });
                    }

                    // TODO reservations.tickets this isn't being populated as there is no call to the reducer
                    if (reservations.tickets) {
                        reservations.tickets.forEach((res) => {
                            if (
                                ((res.tour_id === item.entity_id &&
                                    item.entity_type === 'tour') ||
                                    (res.facility_id === item.entity_id &&
                                        item.entity_type === 'ticketfacility')) &&
                                res.status === 'CONFIRMED'
                            ) {
                                const tdt = startOfDay(res.tour_date);
                                if (isBetweenDays(tdt, startDate, endDate)) {
                                    item.reserved = true;
                                    item.status = 'Reserved';
                                    item.dates.push(
                                        ...[format(tdt, types.FNS_DATE_FORMAT)]
                                    );
                                }
                            }
                        });
                    }

                    if (reservations.permits) {
                        reservations.permits.forEach((issuance) => {
                            if (
                                issuance.permit_id === item.entity_id &&
                                item.entity_type === 'permit' &&
                                issuance.status !== 12 &&
                                issuance.status !== 13
                            ) {
                                const tdt = startOfDay(issuance.start_date);

                                if (isBetweenDays(tdt, startDate, endDate)) {
                                    item.reserved = true;
                                    let status = 'In Progress';

                                    if (
                                        issuance.status === 10 ||
                                        issuance.status === 11
                                    ) {
                                        status = 'Reserved';
                                    }

                                    item.status = status;
                                    item.dates.push(
                                        ...[format(tdt, types.FNS_DATE_FORMAT)]
                                    );
                                }
                            }
                        });
                    }
                });

                startDate = endDate;
            }
        });
    }

    return trip;
}

export default function tripPlanner(state = INITIALSTATE, action) {
    let st = state;
    let filterOptions;
    let destination;

    switch (action.type) {
        case types.TRIP_PLANNER_START_SEARCH_RESULTS:
            st = cloneState(state);
            st.loading = true;
            return st;

        case types.TRIP_PLANNER_END_SEARCH_RESULTS:
            st = cloneState(state);
            st.loading = false;
            return st;

        case types.TRIP_PLANNER_SET_VIEW:
            st = cloneState(state);
            st.view = action.view;
            return st;

        case types.TRIP_PLANNER_SET_ENABLE_OVERVIEW_MODAL:
            st = cloneState(state);
            st.enableOverviewModal = action.enableOverviewModal;
            return st;

        case types.TRIP_PLANNER_SET_DATA_BOOK_MODAL:
            st = cloneState(state);
            st.bookingData = action.bookingData;
            return st;

        case types.TRIP_PLANNER_SET_SHOW_EDIT_ROUTE_MODAL:
            st = cloneState(state);
            st.showEditRouteModal = action.showEditRouteModal;
            return st;

        case types.TRIP_PLANNER_SET_SHOW_TRIP_SHARE_MODAL:
            st = cloneState(state);
            st.showTripShareModal = action.showTripShareModal;
            return st;

        case types.TRIP_PLANNER_SET_SHOW_TRIP_ITEM_ADD_MODAL:
            st = cloneState(state);
            st.showTripItemAddModal = action.showTripItemAddModal;
            st.tripItemToAdd = action.tripItemToAdd;
            return st;

        case types.GET_DIRECTIONS:
            st = cloneState(state);
            st.directions = action.directions;
            if (
                st.directions &&
                st.directions.routes &&
                st.directions.routes.length > 0
            ) {
                st.directions.routes[0].legs.forEach((leg, index) => {
                    if (
                        st.trip &&
                        st.trip.destinations &&
                        st.trip.destinations.length > index
                    ) {
                        st.trip.destinations[index + 1].distance = leg.distance;
                        st.trip.destinations[index + 1].travel_duration = leg.duration;
                    }
                });
            }
            return st;

        case types.GET_DIRECTIONS_ERROR:
            st = cloneState(state);
            st.error = action.payload;
            return st;

        case types.TRIP_PLANNER_SET_START_DATE:
            st = cloneState(state);
            st.trip.start_date = action.date;
            return st;

        case types.TRIP_PLANNER_SET_END_DATE:
            st = cloneState(state);
            st.trip.end_date = action.date;
            return st;

        case types.TRIP_PLANNER_SET_NAME:
            st = cloneState(state);
            st.trip.name = action.name;
            return st;

        case types.TRIP_PLANNER_SET_ACTIVE_DATE:
            st = cloneState(state);
            st.activeDate = action.activeDate;
            return st;

        case types.TRIP_PLANNER_SET_FILTERS_VISIBLE:
            st = cloneState(state);
            st.filtersVisible = action.filtersVisible;
            return st;

        case types.TRIP_PLANNER_SET_ACTIVE_DESTINATION_INDEX:
            st = cloneState(state);
            if (action.activeDestinationMapIndex) {
                st.activeDestinationMapIndex = action.activeDestinationMapIndex;
                st.activeDestinationIndex = action.activeDestinationMapIndex;
            }
            if (action.activeDestinationListIndex) {
                st.activeDestinationListIndex = action.activeDestinationListIndex;
                st.activeDestinationIndex = action.activeDestinationListIndex;
            }
            if (action.activeDestinationItineraryIndex) {
                st.activeDestinationItineraryIndex =
                    action.activeDestinationItineraryIndex;
                st.activeDestinationIndex = action.activeDestinationItineraryIndex;
            }
            st.activeItemIndex = null;
            return st;

        case types.TRIP_PLANNER_SET_ACTIVE_ITEM_INDEX:
            st = cloneState(state);
            st.activeDestinationIndex = action.activeDestinationIndex;
            st.activeDestinationMapIndex = action.activeDestinationIndex;
            st.activeDestinationListIndex = action.activeDestinationIndex;
            st.activeItemIndex = action.activeItemIndex;
            return st;

        case types.TRIP_PLANNER_SET_RADIUS:
            st = cloneState(state);
            if (st.trip && st.trip.destinations) {
                if (action.radius) {
                    st.trip.radius = toNumber(action.radius);
                } else {
                    st.trip.radius = null;
                }
                st.trip.destinations.forEach((d) => {
                    const dst = d;
                    dst.start = 0;
                });
            }
            return st;

        case types.TRIP_PLANNER_SET_GUEST_COUNTS:
            st = cloneState(state);
            st.trip.guest_counts = action.guestCounts;
            return st;

        case types.TRIP_PLANNER_ADD_DESTINATION:
            st = cloneState(state);
            st.trip.destinations[st.trip.destinations.length] = action.destination;
            if (!st.trip?.items?.length) st.trip.items = [];
            if (action.destination?.items?.length) {
                st.trip.items = [...st.trip.items, ...action.destination.items];
            }

            return st;
        case types.UPDATE_TRIP_PLANNER_CRITERIA:
            st = cloneState(state);
            st[action.name] = action.value;
            return st;
        case types.TRIP_PLANNER_UPDATE_DESTINATION:
            st = cloneState(state);
            st.trip.destinations[action.index] = action.destination;
            return st;

        case types.TRIP_PLANNER_DELETE_DESTINATION:
            st = cloneState(state);
            if (action.index >= 0 && action.index < st.trip.destinations.length) {
                st.trip.destinations.splice(action.index, 1);
            }
            return st;

        case types.TRIP_PLANNER_UPDATE_DESTINATION_SEARCH_CRITERIA:
            st = cloneState(state);
            destination = Object.assign(
                st.trip.destinations[action.destinationIndex],
                action.params
            );
            st.trip.destinations[action.index] = destination;
            return st;

        case types.TRIP_PLANNER_MOVE_DESTINATION:
            st = cloneState(state);
            const to = st.trip.destinations[action.toIndex];
            st.trip.destinations[action.toIndex] = st.trip.destinations[action.fromIndex];
            st.trip.destinations[action.fromIndex] = to;
            return st;

        case types.TRIP_PLANNER_ADD_FILTER:
            st = cloneState(state);
            filterOptions = st.uiFilters[action.uiFilterName];
            filterOptions[action.uiFilterOption.key] = action.uiFilterOption;
            st.uiFilters[action.uiFilterName] = filterOptions;
            st.uiFilters.hasFilters = true;
            st.uiFilters.lastAppliedFilter = action.uiFilterName;
            st.start = 0;
            return st;

        case types.TRIP_PLANNER_SET_FILTER:
            st = cloneState(state);
            filterOptions = {};
            filterOptions[action.uiFilterOption.key] = action.uiFilterOption;
            st.uiFilters[action.uiFilterName] = filterOptions;
            st.uiFilters.hasFilters = true;
            st.uiFilters.lastAppliedFilter = action.uiFilterName;
            st.start = 0;
            return st;

        case types.TRIP_PLANNER_REMOVE_FILTER:
            st = cloneState(state);
            filterOptions = st.uiFilters[action.uiFilterName];
            delete filterOptions[action.uiFilterOption.key];
            st.uiFilters[action.uiFilterName] = filterOptions;
            st.uiFilters.hasFilters = checkUIFiltersPresent(st);
            st.uiFilters.lastAppliedFilter = action.uiFilterName;
            st.start = 0;
            return st;

        case types.GET_ITINERARY:
            st = cloneState(state);
            st.interary = action.payload;
            return st;

        case types.GET_ITINERARY_ERROR:
            st = cloneState(state);
            st.error = action.payload;
            return st;

        case types.TRIP_PLANNER_SET_TRIP_SAVED:
            st = cloneState(state);
            st.lastSavedDate = format(new Date(), types.FNS_DATE_FORMAT);

            return st;

        case types.TRIP_PLANNER_ADD_ITEM:
            st = cloneState(state);
            if (st.trip.destinations.length > action.destinationIndex) {
                // check if item already exists in destination, and if it doesn't then add it
                let found = false;
                destination = st.trip.destinations[action.destinationIndex];

                destination.items.forEach((item) => {
                    if (
                        item.entity_id === action.item.entity_id &&
                        item.entity_type === action.item.entity_type
                    ) {
                        found = true;
                    }
                });

                if (!found) {
                    const item = action.item;
                    item.lat = toNumber(action.item.lat);
                    item.lng = toNumber(action.item.lng);
                    destination.items.push(item);
                }
            }
            return st;

        case types.TRIP_PLANNER_DELETE_ITEM:
            st = cloneState(state);
            if (action.destinationIndex) {
                // if destination index is passed, then delete item from that destination
                if (st.trip.destinations.length > action.destinationIndex) {
                    // find item in destination and if found then remove it
                    let itemIndex = -1;
                    destination = st.trip.destinations[action.destinationIndex];
                    destination.items.forEach((item, index) => {
                        if (
                            item.entity_id === action.item.entity_id &&
                            item.entity_type === action.item.entity_type
                        ) {
                            itemIndex = index;
                        }
                    });
                    if (itemIndex >= 0) {
                        destination.items.splice(itemIndex, 1);
                    }
                }
            } else {
                // if destination index was not passed, then delete item from all trip destinations
                st.trip.destinations.forEach((d) => {
                    destination = d;
                    // find item in destination and if found then remove it
                    let itemIndex = -1;
                    destination.items.forEach((item, index) => {
                        if (
                            item.entity_id === action.item.entity_id &&
                            item.entity_type === action.item.entity_type
                        ) {
                            itemIndex = index;
                        }
                    });
                    if (itemIndex >= 0) {
                        destination.items.splice(itemIndex, 1);
                    }
                });
            }
            return st;

        case types.TRIP_PLANNER_ADD_SEARCH_FILTER:
            st = cloneState(state);
            filterOptions = st.uiFilters[action.uiFilterName];
            filterOptions[action.uiFilterOption.key] = action.uiFilterOption;
            st.uiFilters[action.uiFilterName] = filterOptions;
            st.uiFilters.hasFilters = true;
            st.uiFilters.lastAppliedFilter = action.uiFilterName;
            st.start = 0;
            return st;

        case types.TRIP_PLANNER_SET_SEARCH_FILTER:
            st = cloneState(state);
            filterOptions = {};
            filterOptions[action.uiFilterOption.key] = action.uiFilterOption;
            st.uiFilters[action.uiFilterName] = filterOptions;
            st.uiFilters.hasFilters = true;
            st.uiFilters.lastAppliedFilter = action.uiFilterName;
            st.start = 0;
            return st;

        case types.TRIP_PLANNER_REMOVE_SEARCH_FILTER:
            st = cloneState(state);
            filterOptions = st.uiFilters[action.uiFilterName];
            delete filterOptions[action.uiFilterOption.key];
            st.uiFilters[action.uiFilterName] = filterOptions;
            st.uiFilters.hasFilters = checkUIFiltersPresent(st);
            st.uiFilters.lastAppliedFilter = action.uiFilterName;
            st.start = 0;
            return st;

        case types.TRIP_PLANNER_RESET_FILTERS:
            st = cloneState(state);
            st.filters = [];
            st.start = 0;
            st.uiFilters = {
                agencies: {},
                hasFilters: false,
                activities: {},
                accommodations: {},
                campsiteUseTypes: {},
                campsiteTypes: {},
                campsiteAmenities: {},
                campsiteElectricityHookup: {},
                campsiteVehicleLength: {},
                campsiteEquipment: {},
                occupants: {},
                inventoryTypes: {},
                tourTypes: {},
                tourAccessible: {},
                tourDifficulty: {},
                tourDuration: {},
                tourPrice: {},
                tourTime: {},
                permitTypes: {},
                reservable: {},
                lastAppliedFilter: null,
            };
            st.fg = [];
            return st;

        case types.TRIP_PLANNER_SET_TRIP:
            st = cloneState(state);

            st.trip = action.trip;
            if (!st.trip.radius) {
                st.trip.radius = types.DEFAULT_RADIUS;
            }
            return st;

        case types.TRIP_PLANNER_SET_TRIP_ID:
            st = cloneState(state);
            st.trip.trip_id = action.trip_id;
            return st;

        case types.TRIP_PLANNER_LOAD_USER_TRIPS:
            st = cloneState(state);
            st.trips = action.trips;
            return st;

        case types.TRIP_PLANNER_LOAD_USER_PREFERENCES:
            st = cloneState(state);
            st.preferences = action.preferences;
            return st;

        case types.TRIP_PLANNER_SET_HIDE_INTRO:
            st = cloneState(state);
            st.preferences.hide_intro = action.hide_intro;
            return st;

        case types.TRIP_PLANNER_SET_HIDE_BOOKING_INTRO:
            st = cloneState(state);
            st.preferences.hide_booking_intro = action.hide_booking_intro;
            return st;

        case types.TRIP_PLANNER_LOAD_TRIP_IDEAS:
            st = cloneState(state);
            st.tripIdeas = action.trips;
            return st;

        case types.TRIP_PLANNER_UPDATE_CRITERIAS:
            st = cloneState(state);
            for (const name in action) {
                if (action[name]) {
                    st[name] = action[name];
                }
            }
            return st;

        case types.TRIP_PLANNER_LOAD_ITEMS_DETAILS:
            st = cloneState(state);
            st.trip.destinations.forEach((d) => {
                destination = d;
                const id = getSearchDocId(destination);

                if (id) {
                    action.results.forEach((result) => {
                        if (result.id === id) {
                            copyItemDetails(destination, result);
                        }
                    });
                }

                destination.items.forEach((item) => {
                    const docId = getSearchDocId(item);
                    if (docId) {
                        action.results.forEach((result) => {
                            if (result.id === docId) {
                                copyItemDetails(item, result);
                            }
                        });
                    }
                });
            });
            return st;

        case types.TRIP_PLANNER_LOAD_CAMPING_RESERVATIONS:
            st = cloneState(state);
            st.reservations.camping = action.reservations;
            st.trip = checkTripReservationStatus(st.trip, st.reservations);
            return st;

        case types.TRIP_PLANNER_LOAD_TICKET_RESERVATIONS: // TODO this isn't being called
            st = cloneState(state);
            st.reservations.tickets = action.reservations;
            st.trip = checkTripReservationStatus(st.trip, st.reservations);
            return st;

        case types.TRIP_PLANNER_LOAD_PERMIT_ISSUANCES:
            st = cloneState(state);
            st.reservations.permits = action.issuances;
            st.trip = checkTripReservationStatus(st.trip, st.reservations);
            return st;

        case types.TRIP_PLANNER_SET_SEARCH_LOCATION:
            st = cloneState(state);
            st.searchLocation = action.searchLocation;
            return st;

        default:
            return st;
    }
}
